import { useEffect, useRef, useState } from 'react'
import * as Tooltip from '@radix-ui/react-tooltip'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, HelpCircle, Loader2, Sparkles, Trash2 } from 'lucide-react'

import { dramaApi, dramaQueryKeys } from '@/lib/api/drama'
import type { DramaProjectStatus } from '@/types/drama'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { EpisodeUploader } from '@/components/drama/EpisodeUploader'
import { StoryOutlinePanel } from '@/components/drama/StoryOutlinePanel'
import { SegmentSearchPanel } from '@/components/drama/SegmentSearchPanel'
import { CharacterTable, isUnnamed } from '@/components/drama/CharacterTable'
import { ProposalsList } from '@/components/drama/ProposalsList'
import { RenderWorkbench } from '@/components/drama/RenderWorkbench'
import { FinalCutsPanel } from '@/components/drama/FinalCutsPanel'
import { LangToggle } from '@/components/drama/LangToggle'
import { dramaT } from '@/components/drama/i18n'
import { useDramaLang } from '@/lib/useDramaLang'
import { DeleteProjectDialog } from '@/components/drama/DeleteProjectDialog'
import { PreprocessProgressPanel } from '@/components/drama/PreprocessProgressPanel'
import { GenerateMixForm } from '@/components/drama/GenerateMixForm'
import { toast } from '@/components/ui/toaster'

const STATUS_VARIANT: Record<DramaProjectStatus, 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'info'> = {
  draft: 'secondary',
  uploading: 'warning',
  preprocessing: 'warning',
  ready: 'success',
  failed: 'destructive',
  archived: 'outline' as 'secondary',
}

// Small "?" hint next to a tab label. Uses a real Radix tooltip (the native
// `title` attribute was unreliable — slow/no-show), so hovering reliably shows
// the function description.
function TabHint({ text }: { text: string }) {
  return (
    <Tooltip.Provider delayDuration={120}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span
            role="img"
            aria-label={text}
            onClick={(e) => e.stopPropagation()}
            className="ml-1 inline-flex items-center text-muted-foreground/60 hover:text-muted-foreground"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="top"
            sideOffset={6}
            className="z-50 max-w-xs rounded-md bg-foreground px-2.5 py-1.5 text-xs leading-snug text-background shadow-md"
          >
            {text}
            <Tooltip.Arrow className="fill-foreground" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  )
}

export function DramaProjectDetailPage() {
  const { projectId = '' } = useParams<{ projectId: string }>()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('episodes')
  const [mixDialogOpen, setMixDialogOpen] = useState(false)
  const [activeRenderCode, setActiveRenderCode] = useState<string | null>(null)
  // [REFACTOR-2026-06-07] One global EN/中文 switch for the whole drama feature,
  // persisted + shared across routes (detail / list / dialogs) via useDramaLang.
  const [lang, setLang] = useDramaLang()
  const t = dramaT(lang)

  const { data: detail, isLoading } = useQuery({
    queryKey: dramaQueryKeys.detail(projectId),
    queryFn: () => dramaApi.getProjectDetail(projectId),
    enabled: !!projectId,
    // Poll while any episode is still being preprocessed (status=processing
    // on the episode row reflects what start_preprocess sets, and _run_batch
    // transitions to ready/failed at the end). Note: this stops once all
    // episodes flip to ready, which is BEFORE the project-wide stages
    // (story outline, cast, …) finish — the progress query below keeps
    // polling through them and invalidates this query when truly done.
    refetchInterval: (q) => {
      const eps = q.state.data?.episodes ?? []
      // Poll while episodes are preprocessing OR a render is exporting. The
      // export request is synchronous and can exceed the gateway timeout, so we
      // can't rely on its response to refresh — polling render status flips the
      // UI to success/failed whenever FFmpeg finishes, no manual refresh needed.
      const renders = q.state.data?.renders ?? []
      const busy =
        eps.some((e) => e.status === 'processing') ||
        renders.some((r) => r.status === 'running' || r.status === 'pending')
      return busy ? 3000 : false
    },
  })

  const episodes = detail?.episodes ?? []
  const hasReady = episodes.some((e) => e.status === 'ready')
  const isPreprocessing = episodes.some((e) => e.status === 'processing')

  // Progress query: keep it alive for the whole project lifecycle and let the
  // refetchInterval decide cadence from the run's OWN state. The previous
  // version gated `enabled` on episode statuses / project.status, so it went
  // dead the moment all episodes turned ready — i.e. exactly when the
  // project-wide "final" stages start. That's why completion only appeared
  // after a manual refresh. Now we poll until the backend reports `done`.
  const { data: progress } = useQuery({
    queryKey: dramaQueryKeys.progress(projectId),
    queryFn: () => dramaApi.getProgress(projectId),
    enabled: !!projectId,
    refetchInterval: (q) => {
      const d = q.state.data
      // Poll only while a run is actually in flight; a finished or
      // never-started project returns running=false/undefined → no polling.
      return d?.running && !d.done ? 1500 : false
    },
  })

  // Surface completion as a toast so the user gets immediate feedback when
  // they were on a different tab.
  const lastDoneRef = useRef<boolean>(false)
  useEffect(() => {
    if (!progress) return
    if (progress.done && !lastDoneRef.current) {
      lastDoneRef.current = true
      if (progress.error) {
        toast({
          title: t('toast.analysisDoneErr'),
          description: progress.error,
          variant: 'destructive',
        })
      } else {
        toast({
          title: t('toast.analysisDone'),
          description: t('toast.analysisDoneDesc'),
        })
      }
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
    }
    if (!progress.done) {
      lastDoneRef.current = false
    }
  }, [progress, queryClient, projectId])

  if (isLoading || !detail) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const project = detail.project

  // Show the rich progress panel while a batch is running OR has events to
  // show. We also keep it visible briefly after `done: true` so the user sees
  // the "complete" state before the panel collapses.
  const showProgress =
    isPreprocessing ||
    (progress != null && (progress.running === true || (progress.stages?.length ?? 0) > 0))

  // Until at least one episode is ready, downstream tabs would be empty —
  // hide them to keep the user focused on review + start.
  const downstreamReady = hasReady

  return (
    <div className="space-y-4">
      <Link
        to="/drama"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        {t('detail.back')}
      </Link>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{project.title}</h1>
          <p className="text-xs text-muted-foreground">
{t('detail.projectId')} <code>{project.id}</code>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={STATUS_VARIANT[project.status] ?? 'secondary'}>{project.status}</Badge>
          {downstreamReady && (
            <Button size="sm" onClick={() => setMixDialogOpen(true)}>
              <Sparkles className="w-4 h-4 mr-1" /> {t('detail.newMix')}
            </Button>
          )}
          <DeleteProjectDialog
            projectId={projectId}
            projectTitle={project.title}
            trigger={
              <Button variant="outline" size="sm" className="text-destructive">
                <Trash2 className="w-4 h-4 mr-1" /> {t('detail.delete')}
              </Button>
            }
          />
        </div>
      </div>

      <Dialog open={mixDialogOpen} onOpenChange={setMixDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-spectrum-magenta" />
              {t('detail.newMixTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('detail.newMixDesc')}
            </DialogDescription>
          </DialogHeader>
          <GenerateMixForm
            projectId={projectId}
            lang={lang}
            variant="dialog"
            onComplete={() => {
              setMixDialogOpen(false)
              setTab('mix-plans')
            }}
          />
        </DialogContent>
      </Dialog>

      {showProgress && progress && (
        <PreprocessProgressPanel progress={progress} totalEpisodes={episodes.length} lang={lang} />
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <TabsList>
          <TabsTrigger value="episodes">
            {t('tab.episodes')} ({detail.episodes.length})
            <TabHint text={t('tabHint.episodes')} />
          </TabsTrigger>
          {downstreamReady && (
            <>
              <TabsTrigger value="story">
                {t('tab.story')}
                <TabHint text={t('tabHint.story')} />
              </TabsTrigger>
              <TabsTrigger value="characters">
                {t('tab.characters')} ({detail.characters.filter((c) => !isUnnamed(c)).length})
                <TabHint text={t('tabHint.characters')} />
              </TabsTrigger>
              <TabsTrigger value="segments">
                {t('tab.segments')} ({detail.segment_count})
                <TabHint text={t('tabHint.segments')} />
              </TabsTrigger>
              <TabsTrigger value="mix-plans">
                {t('tab.mixPlans')} ({detail.edit_plans.length})
                <TabHint text={t('tabHint.mixPlans')} />
              </TabsTrigger>
              <TabsTrigger value="renders">
                {t('tab.renders')} ({detail.renders.length})
                <TabHint text={t('tabHint.renders')} />
              </TabsTrigger>
              <TabsTrigger value="outputs">
                {t('tab.outputs')} ({(detail.final_cuts || []).length})
                <TabHint text={t('tabHint.outputs')} />
              </TabsTrigger>
            </>
          )}
          </TabsList>
          {downstreamReady && (
            <LangToggle lang={lang} onChange={setLang} />
          )}
        </div>

        <TabsContent value="episodes">
          <EpisodeUploader
            projectId={projectId}
            existingEpisodes={detail.episodes}
            language={project.language}
            lang={lang}
          />
        </TabsContent>

        {downstreamReady && (
          <>
            <TabsContent value="story">
              <StoryOutlinePanel projectId={projectId} outline={detail.outline} lang={lang} />
            </TabsContent>

            <TabsContent value="characters">
              <CharacterTable projectId={projectId} characters={detail.characters} lang={lang} />
            </TabsContent>

            <TabsContent value="segments">
              <SegmentSearchPanel
                projectId={projectId}
                availableCharacters={detail.characters}
                initialSegments={detail.segments}
                lang={lang}
              />
            </TabsContent>

            <TabsContent value="mix-plans">
              <ProposalsList
                projectId={projectId}
                plans={detail.edit_plans}
                segments={detail.segments}
                lang={lang}
                onEnterWorkbench={(renderCode) => {
                  setActiveRenderCode(renderCode)
                  setTab('renders')
                }}
              />
            </TabsContent>

            <TabsContent value="renders">
              <RenderWorkbench
                projectId={projectId}
                renders={detail.renders}
                segments={detail.segments}
                lang={lang}
                activeRenderCode={activeRenderCode}
                onActiveRenderChange={setActiveRenderCode}
              />
            </TabsContent>

            <TabsContent value="outputs">
              <FinalCutsPanel
                projectId={projectId}
                finalCuts={detail.final_cuts}
                editPlans={detail.edit_plans}
                lang={lang}
              />
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  )
}
